import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { fetchWalletPositions } from '../zerionApi';
import { useWallet } from '../contexts/WalletContext';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import ProGate from './ProGate';
import { HiOutlineRefresh, HiOutlineLightningBolt, HiOutlineExclamation, HiOutlineCheck } from 'react-icons/hi';

function fmtUsd(v) {
  if (v == null || isNaN(v)) return '$0.00';
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function getHealthStatus(dark) {
  return {
    healthy: { label: 'Healthy', color: '#0D7A4E', bg: dark ? 'rgba(13,122,78,0.12)' : '#E8F8F2', icon: HiOutlineCheck },
    warning: { label: 'Needs Attention', color: '#8A6A00', bg: dark ? 'rgba(138,106,0,0.12)' : '#FFF8E0', icon: HiOutlineExclamation },
    critical: { label: 'Action Required', color: '#B8001A', bg: dark ? 'rgba(184,0,26,0.12)' : '#FFF0F2', icon: HiOutlineLightningBolt },
  };
}

export default function SmartRebalancingView({ pools }) {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const { account, isConnected } = useWallet();
  const HEALTH_STATUS = getHealthStatus(dark);

  const [address, setAddress] = useState('');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);

  const targetAddress = address || account;

  const analyze = useCallback(async () => {
    if (!targetAddress || !/^0x[0-9a-fA-F]{40}$/.test(targetAddress)) {
      toast.error('Invalid wallet address');
      return;
    }
    setLoading(true);
    try {
      const pos = await fetchWalletPositions(targetAddress);
      setPositions(pos || []);
      setAnalyzed(true);
      toast.success(`Analyzing ${(pos || []).length} positions for rebalancing`);
    } catch {
      toast.error('Analysis failed. Check that the API key is configured.');
    }
    setLoading(false);
  }, [targetAddress]);

  // Rebalancing suggestions
  const suggestions = useMemo(() => {
    if (!positions.length || !pools?.length) return [];

    return positions.map(pos => {
      const posTokens = pos.tokens?.map(t => t.symbol?.toUpperCase()) || [];
      const value = pos.totalValueUsd || 0;
      const change1d = pos.changes?.percent_1d || 0;

      // Find better alternatives
      const alternatives = pools
        .filter(pool => {
          const poolTokens = (pool.tokens || []).map(t => t.toUpperCase());
          return posTokens.some(t => poolTokens.includes(t));
        })
        .sort((a, b) => b.apr - a.apr)
        .slice(0, 3);

      const bestAlt = alternatives[0];

      // Determine health
      let health = 'healthy';
      let reasons = [];

      if (change1d < -5) {
        health = 'critical';
        reasons.push(`Value dropped ${change1d.toFixed(1)}% in 24h`);
      } else if (change1d < -2) {
        health = 'warning';
        reasons.push(`Value dropped ${change1d.toFixed(1)}% in 24h`);
      }

      if (bestAlt && bestAlt.apr > 0 && value > 100) {
        const potentialGain = (value * bestAlt.apr / 100) / 12; // monthly
        const currentApr = pos.apr || 0;
        const improvement = currentApr > 0 ? ((bestAlt.apr - currentApr) / currentApr) * 100 : 100;
        if (potentialGain > 50 || improvement > 20) {
          reasons.push(`Better alternative: ${bestAlt.name} at ${bestAlt.apr.toFixed(2)}% APR`);
          if (health === 'healthy') health = 'warning';
        }
      }

      if (reasons.length === 0) {
        reasons.push('Position looks good');
      }

      return {
        position: pos,
        health,
        reasons,
        alternatives: alternatives.slice(0, 2),
        currentValue: value,
      };
    }).sort((a, b) => {
      const order = { critical: 0, warning: 1, healthy: 2 };
      return order[a.health] - order[b.health];
    });
  }, [positions, pools]);

  const overallHealth = useMemo(() => {
    if (!suggestions.length) return 'healthy';
    if (suggestions.some(s => s.health === 'critical')) return 'critical';
    if (suggestions.some(s => s.health === 'warning')) return 'warning';
    return 'healthy';
  }, [suggestions]);

  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px' }}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 24 : 28,
          color: colors.textPrimary,
          marginBottom: 4,
        }}>
          Smart Rebalancing
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.textSecondary,
        }}>
          Monitor positions and get rebalancing suggestions when yields shift
        </div>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input
          type="text"
          value={address}
          onChange={e => setAddress(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && analyze()}
          placeholder={isConnected ? `Connected: ${account?.slice(0, 8)}...` : 'Enter wallet address (0x...)'}
          style={{
            flex: 1,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            color: colors.textPrimary,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: '12px 16px',
            outline: 'none',
          }}
        />
        <button
          onClick={analyze}
          disabled={loading}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: '#FFFFFF',
            background: colors.green,
            border: 'none',
            borderRadius: 10,
            padding: '0 20px',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <HiOutlineRefresh size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Scanning...' : 'Scan Portfolio'}
        </button>
      </div>

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: 40,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          Analyzing positions for rebalancing opportunities...
        </div>
      )}

      {analyzed && !loading && (
        <>
          {/* Overall health */}
          <div style={{
            background: HEALTH_STATUS[overallHealth].bg,
            border: `1px solid ${HEALTH_STATUS[overallHealth].color}30`,
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            {React.createElement(HEALTH_STATUS[overallHealth].icon, {
              size: 24,
              style: { color: HEALTH_STATUS[overallHealth].color },
            })}
            <div>
              <div style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 20,
                color: HEALTH_STATUS[overallHealth].color,
              }}>
                Portfolio: {HEALTH_STATUS[overallHealth].label}
              </div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                color: colors.textSecondary,
                marginTop: 2,
              }}>
                {suggestions.filter(s => s.health === 'critical').length} critical
                {' \u00B7 '}
                {suggestions.filter(s => s.health === 'warning').length} warnings
                {' \u00B7 '}
                {suggestions.filter(s => s.health === 'healthy').length} healthy
              </div>
            </div>
          </div>

          {/* Suggestions list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {suggestions.map((s, i) => {
              const status = HEALTH_STATUS[s.health];
              return (
                <div key={i} style={{
                  background: colors.surface,
                  borderRadius: 12,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `3px solid ${status.color}`,
                  padding: 16,
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 13,
                      color: colors.textPrimary,
                      fontWeight: 500,
                    }}>
                      {s.position.poolName || s.position.protocolName || 'Position'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 9,
                        fontWeight: 600,
                        color: status.color,
                        background: status.bg,
                        padding: '2px 8px',
                        borderRadius: 3,
                      }}>
                        {status.label.toUpperCase()}
                      </span>
                      <span style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: 16,
                        color: colors.textPrimary,
                      }}>
                        {fmtUsd(s.currentValue)}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    color: colors.textSecondary,
                    marginBottom: 4,
                  }}>
                    {s.position.protocolName} &middot; {s.position.chain}
                  </div>

                  {s.reasons.map((r, j) => (
                    <div key={j} style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 11,
                      color: s.health === 'healthy' ? colors.green : status.color,
                      marginTop: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}>
                      <span style={{ fontSize: 8 }}>{s.health === 'healthy' ? '\u2713' : '\u26A0'}</span>
                      {r}
                    </div>
                  ))}

                  {s.alternatives.length > 0 && s.health !== 'healthy' && (
                    <div style={{
                      marginTop: 10,
                      padding: '8px 12px',
                      background: dark ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 9,
                        color: colors.labelMuted,
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}>
                        ALTERNATIVES
                      </div>
                      {s.alternatives.map((alt, k) => (
                        <div key={k} style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11,
                          color: colors.textPrimary,
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '2px 0',
                        }}>
                          <span>{alt.name} ({alt.protocol} / {alt.chain})</span>
                          <span style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontSize: 13,
                            color: colors.green,
                          }}>
                            {alt.apr.toFixed(2)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {suggestions.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: 40,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: colors.labelMuted,
            }}>
              No positions found to analyze
            </div>
          )}
        </>
      )}
    </motion.div>
  );

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <ProGate feature="smart rebalancing suggestions">
        {content}
      </ProGate>
    </>
  );
}
